'use client';

import { type ReactNode } from 'react';

import { type PostChannelVerificationVerifyResponse } from '@flipster/apis/v1/channel-verification/types';
import { T } from '@flipster/i18n';
import { Dialog } from '@flipster/react/design-system/dialog';
import UserCheckColorIcon from '@flipster/react/icon/generated/UserCheckColorIcon';
import UserErrorColorIcon from '@flipster/react/icon/generated/UserErrorColorIcon';
import { css } from '@flipster/styled-system/css';

function HighlightSource({ source }: { source: string }): ReactNode {
  return <span className={css({ color: 'content.state.warning' })}>{source}</span>;
}

function buildReportHref(channel: string, source: string) {
  const params = new URLSearchParams({ channel, source });
  return `/channel-verification/report?${params.toString()}`;
}

interface SearchResultDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  result: PostChannelVerificationVerifyResponse;
  enteredSource: string;
}

export const OfficialChannelDialog = ({ isOpen, onClose, onExit, result, enteredSource }: SearchResultDialogProps) => {
  if (!result.result) return null;

  return (
    <Dialog.Root isOpen={isOpen} onExit={onExit}>
      <Dialog.Title label={<T id='VIP.r260316.Official_channel' />} leftAddon={<UserCheckColorIcon size={24} />} />
      <Dialog.Content>
        <p>
          <T
            id='VIP.r260316.Entered_source_is_an_official_flipster_channel_please_check_the_selected_channel_description'
            values={{ enteredSource: <HighlightSource source={enteredSource} /> }}
          />
        </p>
      </Dialog.Content>
      <Dialog.Footer direction='vertical'>
        <Dialog.Button
          as='a'
          href={buildReportHref(result.enteredChannel, enteredSource)}
          hierarchy='secondary'
          color='neutral'
          label={<T id='VIP.r260316.Report_phishing_attacks' />}
          onClick={onClose}
        />
        <Dialog.Button hierarchy='primary' label={<T id='VIP.r260316.Got_it_button' />} onClick={onClose} />
      </Dialog.Footer>
    </Dialog.Root>
  );
};

export const UnofficialChannelDialog = ({
  isOpen,
  onClose,
  onExit,
  result,
  enteredSource,
}: SearchResultDialogProps) => {
  if (result.result) return null;

  return (
    <Dialog.Root isOpen={isOpen} onExit={onExit}>
      <Dialog.Title label={<T id='VIP.r260316.Unofficial_channel' />} leftAddon={<UserErrorColorIcon size={24} />} />
      <Dialog.Content>
        <p>
          <T
            id='VIP.r260316.Entered_source_is_not_an_official_flipster_channel_please_check_the_selected_cha_description'
            values={{ enteredSource: <HighlightSource source={enteredSource} /> }}
          />
        </p>
      </Dialog.Content>
      <Dialog.Footer direction='vertical'>
        <Dialog.Button
          as='a'
          href={buildReportHref(result.enteredChannel, enteredSource)}
          hierarchy='secondary'
          color='neutral'
          label={<T id='VIP.r260316.Report_phishing_attacks' />}
          onClick={onClose}
        />
        <Dialog.Button hierarchy='primary' label={<T id='VIP.r260316.Got_it_button' />} onClick={onClose} />
      </Dialog.Footer>
    </Dialog.Root>
  );
};
